import { useMemo, useEffect, useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { selectProgramTemplate } from '../../Store/api/program/programSelectors';
import { selectCurrentUser } from '../../Store/api/auth/authSelectors';
import { useGetApplicationListQuery, useGetTransformedApplicationQuery, useGetApplicationEligibilityQuery } from '../../Store/api/application/applicationApi';
import { selectIsEligibleToSubmit } from '../../Store/api/application/applicationSelectors';
import { useSessionManagement } from "../../ServiceUtils/useSessionManagement";
import { version } from '../../MPP-Common-SPA/common/constants/version';
import { StepRenderer } from "./StepRenderer";
import { usePortalContext } from "../hooks/usePortalContext";
import { useApplicationCardinality } from "../hooks/useApplicationCardinality";
import {
  GoabCircularProgress,
  GoabModal,
  GoabButtonGroup,
  GoabButton
} from "@abgov/react-components";
import { CopyFromPreviousApplication } from "./CopyFromPreviousApplication";
import { InternalApplicationStatuses } from "../../MPP-Common-SPA/common/constants/statuses";
import { useUnsavedChanges } from '../../MPP-Common-SPA/common/unsavedChangesContext';
import { applicationApi } from '../../Store/api/application/applicationApi';
import { URL_SEARCH_PARAMS } from '../../MPP-Common-SPA/common/constants/urlParams';
import { APPLICATION_CATEGORY } from '../../MPP-Common-SPA/common/constants/categories';

const verificationStatusUpdatedPaymentInformationHeader = 'The application could not be submitted';
const verificationStatusUpdatedPaymentInformationContent = 'We’re having a technical issue and couldn’t complete your submission. Please try again.';
const verificationStatusUpdatedPaymentInformationButton = 'Close';

const PortalContainer = ({
  isPreviewMode = false,
  showDebugInformation = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const hint = params.get(URL_SEARCH_PARAMS.Hint);
  const type = params.get(URL_SEARCH_PARAMS.Type);
  const code = params.get(URL_SEARCH_PARAMS.Code);
  const stepName = params.get(URL_SEARCH_PARAMS.Step);
  const {isLoggedIn, isProxyUser} = useSelector(selectCurrentUser);
  const template = useSelector(selectProgramTemplate);
  const {data: applicationList, isLoading: isLoadingApplications } = useGetApplicationListQuery(undefined, {
    skip: !isLoggedIn,
  })
  const { data: applicationData, isLoading, refetch: refetchApplication, isFetching: isApplicationFetching, isUninitialized } = useGetTransformedApplicationQuery(code, {
    skip: !isLoggedIn || !code,
  });

  const { getRefData, getResourceValue, getDynamicForm, clickHandlers, programEvents } = useSessionManagement();

  useEffect(() => {
    if (!isUninitialized && applicationData && isLoggedIn && !!code) {
      refetchApplication();
    } 
  }, [isLoggedIn, code, refetchApplication, isUninitialized]);

  const userApplication = useMemo(() => {
    let application = applicationData ?
    { 
      // applicationData and internalPortal should likely be the only properties required here.
      // It appears that these are currently used to restore an application from storage if a session expires.
      // And re-login occurs. It may be worth refactoring this to remove this complexity.
      programId: applicationData.programId,
      applicationType: applicationData.applicationType,
      applicationTypeId: applicationData.applicationTypeId,
      applicationData: applicationData.applicationData,
      applicationId: applicationData.applicationId ?? applicationData.id,
      internalStatus: applicationData.internalStatus,
      uniqueApplicationCode: applicationData.uniqueApplicationCode,
      internalPortal: applicationData.internalPortal,
    } : {};

    // If hint and type are 'event', try to match an event and set applicationType
    if (hint && type === APPLICATION_CATEGORY.Event && programEvents && programEvents.length > 0) {
      const matchedEvent = programEvents.find(evt => evt.code === hint);
      if (matchedEvent) {
        application = {
          ...application,
          applicationType: matchedEvent.code,
          applicationTypeId: matchedEvent.id,
        };
      }
    }

    return application;
  }, [applicationData, isLoading, hint, type, isUninitialized, programEvents]);

  // Retrieved from api and is reset on submit, so shouldn't persist in applicationDataJson/userApplication
  const statusTransitionEvent = applicationData?.statusTransitionEvent;
  const verificationStatusChangeEvent = applicationData?.verificationStatusChangeEvent;
  const followUpInstruction = applicationData?.followUpInstruction;
  const userDataChanged = applicationData?.userDataChanged;

  const portalContext = usePortalContext({ userApplication, refetchApplication, isUninitialized, programEvents });
  
  // Careful removing unused variables from portal context as these are used during form definition parsing.
  // Would possibly be a good idea to centralize all portal variables and click handlers here as they can be found in various places currently.
  const { 
    applicationType, 
    isBusy, 
    failedSaved, 
    setFailedSaved, 
    currentStep, 
    onNext, 
    onPrevious, 
    moveToSubmissionRequirement, 
    moveToStep, 
    specialStep, 
    replaceUserApplication,
    isProgramEventMappingLoading,
    isEventsEnabled,
    isActivePortal,
    isActiveEvent,
    activeEvents,
    selectedEvent
  } = portalContext ?? {};

  const isEligible = useSelector(
    applicationType
      ? selectIsEligibleToSubmit(applicationType)
      : () => true
  );

  const { isLoading: isEligibilityLoading } = useGetApplicationEligibilityQuery(applicationType, {
    skip: !isLoggedIn || !applicationType,
  });

  const {
    allowCopyFromPreviousApplication,
    previousApplication,
  } = useApplicationCardinality({
    ...portalContext,
    template,
    isLoggedIn,
    currentStep,
  });

  const { hasUnsavedChanges, clearDirty } = useUnsavedChanges();

  useEffect(() => {
    const selectedApplicationType = applicationType;

    if (!applicationData && applicationList?.length > 0 && selectedApplicationType) {
      const existingApplication = applicationList.find((item) => item.applicationType === selectedApplicationType && item.internalStatus !== InternalApplicationStatuses.Withdrawn);
      if (existingApplication) {
        clearDirty();
      }
    }
    if (stepName) {
      moveToStep(stepName);
    }
  }, [applicationList, applicationData, applicationType]);

  useEffect(() => {
    if (!hasUnsavedChanges && !specialStep) { // Don't navigate when in a special step
      const selectedApplicationType = applicationType;
      if (!isProxyUser && !applicationData && applicationList?.filter(app =>
            app.internalStatus !== InternalApplicationStatuses.Withdrawn &&
            app.internalStatus !== InternalApplicationStatuses.Rejected &&
            app.internalStatus !== InternalApplicationStatuses.Cancelled)?.length > 0) {
        if (selectedApplicationType) {
          const existingApplication = applicationList.find((item) => item.applicationType === selectedApplicationType && item.internalStatus !== InternalApplicationStatuses.Withdrawn && item.internalStatus !== InternalApplicationStatuses.Rejected);
          if (existingApplication) {
            // Only perform navigation after hasUnsavedChanges is set to false and uniqueApplicationCode is available
            navigate(`/application?code=${existingApplication.uniqueApplicationCode}`);
          }
        } else {
          navigate('/applications');
        }
      }
    }
  }, [hasUnsavedChanges, navigate, applicationList, applicationData, applicationType, specialStep]);

  useEffect(() => {
    if (isEligible === false) {
      moveToSubmissionRequirement();
    }
  }, [isEligible, moveToSubmissionRequirement]);

  const { error } = portalContext ?? {};

  if (error) {
    return <div>ERROR</div>;
  }

  clickHandlers['next'] = onNext;
  clickHandlers['previous'] = onPrevious;

  const isStepRendererReady =
    !isLoading &&
    !isEligibilityLoading &&
    !isApplicationFetching &&
    !isProgramEventMappingLoading

    // Wait for event mapping to load before rendering
    if (!isStepRendererReady) {
      return <></>;
    }

    return (
    <>
      {isPreviewMode && <h4>{`Preview`}</h4>}
      {showDebugInformation && <div>{`Template Version - ${version}`}</div>}
      <GoabCircularProgress
        variant="fullscreen"
        visible={isBusy}
        message={`${getResourceValue({ key: "RESOURCES@global.loading" })}`}
      />
      <GoabModal
        calloutVariant="emergency"
        heading={verificationStatusUpdatedPaymentInformationHeader}
        role="alertdialog"
        open={!!failedSaved}
        actions={
          <GoabButtonGroup alignment="end">
              <GoabButton type="primary" onClick={() => setFailedSaved('')}>{verificationStatusUpdatedPaymentInformationButton}</GoabButton>
          </GoabButtonGroup>
        }
      >
        {verificationStatusUpdatedPaymentInformationContent}
      </GoabModal>
      <CopyFromPreviousApplication
        allowCopyFromPreviousApplication={allowCopyFromPreviousApplication}
        previousApplication={previousApplication}
        getResourceValue={getResourceValue}
        {...portalContext}
      />
      {isStepRendererReady && <StepRenderer programEvents={programEvents} getResourceValue={getResourceValue} getDynamicForm={getDynamicForm} getRefData={getRefData} clickHandlers={clickHandlers} stepName={stepName} statusTransitionEvent={statusTransitionEvent} verificationStatusChangeEvent={verificationStatusChangeEvent} followUpInstruction={followUpInstruction} userDataChanged={userDataChanged} {...portalContext} />}
    </>
  );
};

export default PortalContainer;
